import { ReactNode, MouseEvent, useState } from 'react';
import { Box, ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import { Badge } from 'melp-design/components';
import { calculateFiltersTotal } from 'utils/Filters';
import { TriggerButton } from './atoms';

interface FilterButtonRenderProps<T> {
  applyFilter: (value?: T) => void;
  value?: T;
  clearFilter: () => void;
  close: () => void;
}

interface Props<T> {
  label: string;
  name?: string;
  value?: T;
  initialValue?: T;
  onChange?: (value?: T) => void;
  onClose?: () => void;
  /**
   * Render filter content. If not set, component assumes that this is a
   * flag-type filter, i.e., clicking on filter button leads to setting
   * filter value to true and clicking on it again - to undefined.
   */
  children?: (props: FilterButtonRenderProps<T>) => ReactNode;
  /**
   * Disables react portal to allow displaying popper within modal.
   */
  disablePortal?: boolean;
  /**
   * Max width of the popper content. Defaults to 300.
   */
  contentMaxWidth?: number;
}

export const FilterButton = <T extends any>({
  value,
  initialValue,
  onChange,
  label,
  children,
  disablePortal,
  contentMaxWidth = 300,
  onClose,
}: Props<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isOpen = Boolean(anchorEl);

  const id = isOpen ? 'filter-popover' : undefined;
  const appliedValuesCount = calculateFiltersTotal({ value });
  const isActive = Boolean(appliedValuesCount);

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleApply = (newValue?: T) => {
    onChange?.(newValue);
    handleClose();
  };

  const handleClear = () => {
    onChange?.(initialValue);
    handleClose();
  };

  const handleTrigger = (event: MouseEvent<HTMLButtonElement>) => {
    if (children) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    } else {
      handleApply(!value ? (true as T) : undefined);
    }
  };

  return (
    <>
      <Badge
        badgeContent={appliedValuesCount}
        invisible={!isActive}
        sx={{ '.MuiBadge-badge': { right: 3 } }}
      >
        <TriggerButton
          id={id}
          label={label}
          isActive={isActive}
          onTrigger={handleTrigger}
          onClear={() => handleClear()}
        />
      </Badge>

      <Popper
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ 'z-index': 1500 }}
        disablePortal={disablePortal}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Box mt={1} mr={1}>
              <Paper elevation={8}>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box sx={{ minWidth: 100, maxWidth: contentMaxWidth }}>
                    {children?.({
                      value,
                      applyFilter: handleApply,
                      clearFilter: handleClear,
                      close: handleClose,
                    })}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Box>
          </Grow>
        )}
      </Popper>
    </>
  );
};
